import {
  CATEGORY_FILTER_GROUP_IDS,
  affiliationValues,
  filterOptionField,
  filterOptionId,
  flatSelectionValues,
  isSelectionAll,
  normalizeCalendarFilters,
} from '@/js/core/utils/calendar-filter-state';

/**
 * Build the Calendar Listing chips from the current filter state.
 */
export function calendarFilterChips(filterGroups = [], filters = {}) {
  const normalizedFilters = normalizeCalendarFilters(filters);
  const visibleGroups = filterGroups.filter(group => normalizedFilters[group.id]);

  if (!visibleGroups.some(group => !isSelectionAll(normalizedFilters[group.id]))) {
    return [];
  }

  return [
    ...categoryFilterChips(visibleGroups, normalizedFilters),
    ...nonCategoryFilterChips(visibleGroups, normalizedFilters),
  ];
}

function categoryFilterChips(filterGroups, filters) {
  const groupsById = new Map(filterGroups.map(group => [group.id, group]));

  return CATEGORY_FILTER_GROUP_IDS.flatMap(groupId => {
    const group = groupsById.get(groupId);
    if (!group) return [];

    const selection = filters[groupId];
    if (isSelectionAll(selection)) {
      return [allFilterChip(group)];
    }

    return selectedOptionChips(group, flatSelectionValues(selection));
  });
}

function nonCategoryFilterChips(filterGroups, filters) {
  return filterGroups.flatMap(group => {
    if (CATEGORY_FILTER_GROUP_IDS.includes(group.id)) return [];

    const selection = filters[group.id];
    if (!selection || isSelectionAll(selection)) return [];

    if (group.type === 'affiliations' || group.id === 'affiliations') {
      return selectedAffiliationChips(group, affiliationValues(selection));
    }

    return selectedOptionChips(group, flatSelectionValues(selection));
  });
}

function allFilterChip(group) {
  return {
    key: `${group.id}:all`,
    id: 'all',
    label: group.allLabel,
    groupId: group.id,
    field: null,
  };
}

function selectedOptionChips(group, selectedValues) {
  return selectedValuesFromOptions(group, selectedValues).map(option => ({
    key: `${group.id}:${option.id}`,
    id: option.id,
    label: option.label,
    groupId: group.id,
    field: null,
  }));
}

function selectedAffiliationChips(group, selectedValues) {
  return selectedValuesFromOptions(group, selectedValues).map(option => ({
    key: `${group.id}:${option.field}:${option.id}`,
    id: option.id,
    label: option.label,
    groupId: group.id,
    field: option.field,
  }));
}

function selectedValuesFromOptions(group, selectedValues) {
  const usesFields = group.type === 'affiliations' || group.id === 'affiliations';
  const options = selectableOptions(group.options, usesFields);
  const selected = selectedValueSet(selectedValues);
  const chips = [];
  const included = new Set();

  for (const option of options) {
    if (!selected.has(selectedKey(option.field, option.id))) continue;

    chips.push(option);
    included.add(selectedKey(option.field, option.id));
  }

  for (const option of missingSelectedOptions(group, selectedValues, included)) {
    chips.push(option);
  }

  return chips;
}

function selectableOptions(options = [], usesFields = false) {
  return options.flatMap(option => {
    if (option.sectionTitle && option.choices) {
      return selectableOptions(option.choices, usesFields);
    }

    if (typeof option === 'string') {
      return [{ id: option, label: option, field: null }];
    }

    return [
      {
        id: filterOptionId(option),
        label: option.label || filterOptionId(option),
        field: usesFields ? filterOptionField(option) : null,
      },
      ...selectableOptions(option.children || [], usesFields),
    ];
  });
}

function selectedValueSet(values) {
  if (Array.isArray(values)) {
    return new Set(values.map(value => selectedKey(null, value)));
  }

  return new Set(
    Object.entries(values).flatMap(([field, fieldValues]) =>
      fieldValues.map(value => selectedKey(field, value))
    )
  );
}

function missingSelectedOptions(group, selectedValues, included) {
  if (Array.isArray(selectedValues)) {
    return selectedValues
      .filter(value => !included.has(selectedKey(null, value)))
      .map(value => ({ id: value, label: value, field: null }));
  }

  return Object.entries(selectedValues).flatMap(([field, fieldValues]) =>
    fieldValues
      .filter(value => !included.has(selectedKey(field, value)))
      .map(value => ({
        id: value,
        label: value,
        field: group.type === 'affiliations' || group.id === 'affiliations' ? field : null,
      }))
  );
}

function selectedKey(field, value) {
  return `${field || ''}:${String(value)}`;
}
